import { getDisplayTimeZone } from "./timezone"
import type { InsuranceMember } from "./insuranceTypes"
import { InsuranceBookingStep } from "./pages/InsuranceBookingStep"
import { BookingBreadcrumbProvider, type BookingEntrySource } from "./Breadcrumbs"
import type { BookingPreferences, SelectedSlot } from "./bookingFlowMachine"
import { bookingFlowMachine } from "./bookingFlowMachine"
import { bookingIneligibilityReason, isBookingConsentRequired, isBookingSessionExpired } from "./errorReporting"
import { deriveConsentGate } from "./consentGate"
import {
  BookedAppointment,
  ConsentType,
  ScreeningAppointmentType,
  ScreeningProgram,
  useBeginBooking,
  useInitiateBookingPayment,
  useBookAppointment,
  AVAILABLE_DATES_QUERY_KEY,
  AVAILABILITY_FOR_DATE_QUERY_KEY,
  useConsentStatus,
  useScreeningQuestions
} from "./hooks"
import type { InsuranceBridgeConfig } from "./pages/InsuranceCheckPage"
import { PaymentPage } from "./pages/PaymentPage"
import { PhotoIdPage } from "./pages/PhotoIdPage"
import { BookedPage } from "./pages/BookedPage"
import { BookingFlowError } from "./pages/BookingFlowError"
import { ConsentPage } from "./pages/ConsentPage"
import { HealthQuestionsPage } from "./pages/HealthQuestionsPage"
import { LoadingPage } from "./pages/LoadingPage"
import { NotEligiblePage } from "./pages/NotEligiblePage"
import { SelectPreferencesPage } from "./pages/SelectPreferencesPage"
import { useQueryClient } from "@tanstack/react-query"
import { useMachine } from "@xstate/react"
import React from "react"
import {
  BookingTrackingContext,
  useTrackBookingScreenView,
  useBookingTracking,
  type BookingTrackingCallbacks
} from "./tracking"

type BookingFlowProps = {
  insuranceMember?: InsuranceMember
  bridgeConfig?: InsuranceBridgeConfig
  insuranceEntry?: boolean
  tracking?: BookingTrackingCallbacks
  // The program the member is booking into — drives the screening question set.
  program: ScreeningProgram
  enterprisePaymentsEnabled: boolean
  // The member's legal name for the HIPAA "My name:" line (may be empty). Passed in rather than read
  // from app state — member identity is the host's concern, like navigation below.
  memberName: string
  // Navigation is the host's concern — these come in as callbacks rather than the
  // package importing the router.
  onReturnToProviderResults?: () => void
  entrySource?: BookingEntrySource
  // Sends the member to Carrot Academy's care options from the not-eligible page.
  onExploreCareOptions?: () => void
  onBackToEntry?: () => void
  timeZone?: string
}

// Resolves the eligibility gate and booking context before starting the machine, so the init
// eligibility gate and the `alreadyConsented` guard decide on real values rather than defaults.
export function BookingFlow({ tracking, entrySource, ...props }: BookingFlowProps): JSX.Element {
  const inherited = useBookingTracking()
  return (
    <BookingTrackingContext.Provider value={tracking ?? inherited}>
      <BookingBreadcrumbProvider
        entrySource={entrySource ?? (props.onReturnToProviderResults ? "searchResults" : "carrotPlan")}
        onReturn={props.onReturnToProviderResults}
      >
        <BookingFlowContents {...props} />
      </BookingBreadcrumbProvider>
    </BookingTrackingContext.Provider>
  )
}

function BookingFlowContents({
  program,
  memberName,
  onReturnToProviderResults,
  onExploreCareOptions,
  onBackToEntry,
  timeZone,
  enterprisePaymentsEnabled,
  insuranceEntry = false,
  bridgeConfig,
  insuranceMember
}: BookingFlowProps): JSX.Element {
  const consentStatus = useConsentStatus()
  const eligibility = useBeginBooking(program)
  // Resolve the appointment type as soon as eligibility lands so the screening-question set is
  // fetched for the right offering (defaults to initial while eligibility loads; the gate below holds
  // until both settle, and the machine is keyed on appointmentType so it remounts if it changes).
  const appointmentType: ScreeningAppointmentType = eligibility.data?.appointmentType ?? "initial"
  // Do not send the temporary `initial` default to the guarded screening endpoint. Screening starts
  // only after the server resolves an eligible member's authoritative appointment type.
  const shouldLoadScreeningQuestions = eligibility.data?.eligible === true
  const screeningQuestions = useScreeningQuestions(program, appointmentType, shouldLoadScreeningQuestions)

  // A known-data ineligibility (eligible === false) routes straight to notEligible via the machine's
  // eligibilityGate — that path needs neither consent nor screening data, so don't hold the member on
  // the loading screen waiting for those unrelated queries to settle.
  const isKnownDataIneligible = eligibility.data?.eligible === false

  if (eligibility.isError) {
    return <BookingFlowError onRetry={eligibility.retry} onExit={onBackToEntry} exitLabel="Go back" />
  }

  const isLoading =
    eligibility.isLoading || (!isKnownDataIneligible && (consentStatus.isLoading || screeningQuestions.isLoading))

  if (isLoading) {
    return (
      <BookingBreadcrumbProvider stage={insuranceEntry && enterprisePaymentsEnabled ? "coverage" : "booking"}>
        <LoadingPage />
      </BookingBreadcrumbProvider>
    )
  }

  // The consent gate: `alreadySignedConsent` skips the consent step when every required consent is
  // satisfied (the annual telehealth one lapses yearly, so it can be false again); `outstandingConsents`
  // is what the consent screen records. On a consent fetch failure this shows the screen and treats
  // all consents as outstanding so the member can still sign — see deriveConsentGate.
  const { alreadySignedConsent, outstandingConsents } = deriveConsentGate(consentStatus.data)

  const eligibilityResult = eligibility.data
  const knownDataExclusionMessageId =
    eligibilityResult && !eligibilityResult.eligible ? eligibilityResult.exclusionMessageId : null
  // Skip the health-questions step when this offering has none (e.g. a follow-up). Default to showing
  // it if the question set failed to load — fail toward the safety gate, never silently skip it.
  const hasScreeningQuestions = screeningQuestions.data ? screeningQuestions.data.length > 0 : true

  return (
    // `useMachine` reads its input only at actor creation, so key the machine on the inputs that
    // define which booking this is — a program switch (X→Y→X with Y cached, so no loading remount)
    // would otherwise keep a stale actor. `alreadySignedConsent` is intentionally excluded: it's
    // read once for the consent-skip guard, and keying on it would remount mid-flow on a consent refetch.
    <BookingMachine
      key={`${program}:${appointmentType}:${knownDataExclusionMessageId ?? ""}:${
        insuranceEntry && enterprisePaymentsEnabled
      }`}
      insuranceMember={insuranceMember}
      bridgeConfig={bridgeConfig}
      insuranceEntry={insuranceEntry && enterprisePaymentsEnabled}
      program={program}
      memberName={memberName}
      alreadySignedConsent={alreadySignedConsent}
      outstandingConsents={outstandingConsents}
      refreshOutstandingConsents={async () =>
        deriveConsentGate((await consentStatus.refetch()).data).outstandingConsents
      }
      appointmentType={appointmentType}
      knownDataExclusionMessageId={knownDataExclusionMessageId}
      hasScreeningQuestions={hasScreeningQuestions}
      onReturnToProviderResults={onReturnToProviderResults}
      onExploreCareOptions={onExploreCareOptions}
      onBackToEntry={onBackToEntry}
      timeZone={timeZone}
      enterprisePaymentsEnabled={enterprisePaymentsEnabled}
    />
  )
}

function BookingMachine({
  program,
  memberName,
  alreadySignedConsent,
  outstandingConsents,
  refreshOutstandingConsents,
  appointmentType,
  knownDataExclusionMessageId,
  hasScreeningQuestions,
  onReturnToProviderResults,
  onExploreCareOptions,
  onBackToEntry,
  timeZone,
  enterprisePaymentsEnabled,
  insuranceEntry = false,
  bridgeConfig,
  insuranceMember
}: BookingFlowProps & {
  alreadySignedConsent: boolean
  refreshOutstandingConsents: () => Promise<ConsentType[]>
  outstandingConsents: ConsentType[]
  appointmentType: ScreeningAppointmentType
  knownDataExclusionMessageId: string | null
  hasScreeningQuestions: boolean
}): JSX.Element | null {
  const [state, send, actor] = useMachine(bookingFlowMachine, {
    input: {
      insuranceEntry,
      alreadySignedConsent,
      program,
      appointmentType,
      knownDataExclusionMessageId,
      hasScreeningQuestions
    }
  })
  const queryClient = useQueryClient()
  const tracking = useBookingTracking()
  const [recoveryConsents, setRecoveryConsents] = React.useState<ConsentType[]>([])
  const [initialGate] = React.useState(() => ({
    type: "initialized" as const,
    appointmentType,
    ineligible: knownDataExclusionMessageId !== null,
    consentSkipped: alreadySignedConsent,
    noQuestions: !hasScreeningQuestions
  }))
  const [trackingReady, setTrackingReady] = React.useState(false)
  React.useLayoutEffect(() => {
    tracking.onEvent(initialGate)
    setTrackingReady(true)
  }, [tracking, initialGate])
  useTrackBookingScreenView(
    state.matches("notEligible") ? { screen: "not_eligible" } : state.matches("booked") ? { screen: "booked" } : null
  )
  const initiatePayment = useInitiateBookingPayment()
  const bookAppointment = useBookAppointment()
  const isSelectingPreferences = state.matches({ seeAvailability: "preferences" })
  const { reset: resetPayment } = initiatePayment
  React.useEffect(() => {
    // Mutation state outlives the preferences page. Clear its error when leaving, while keeping
    // the booking session in machine context so returning and retrying remains idempotent.
    if (!isSelectingPreferences) resetPayment()
  }, [isSelectingPreferences, resetPayment])
  const prepareBooking = (offeringId: string): string => {
    send({ type: "PREPARE_BOOKING", offeringId })
    const bookingId = actor.getSnapshot().context.bookingId
    if (!bookingId) throw new Error("Booking identity unavailable")
    return bookingId
  }
  const paymentPending = React.useRef(false)
  const onContinueToPayment = async (preferences: BookingPreferences, slot: SelectedSlot) => {
    if (paymentPending.current) return
    paymentPending.current = true
    try {
      const bookingId = prepareBooking(preferences.offeringId)
      const payment = await initiatePayment.mutateAsync({
        program,
        bookingId,
        routingBackend: slot.routingBackend,
        appointmentType
      })
      send({ type: "CONTINUE_TO_PAYMENT", preferences, slot, payment })
    } finally {
      paymentPending.current = false
    }
  }
  const onContinue = () => send({ type: "CONTINUE" })
  const onConsentContinue = () => {
    if (!hasScreeningQuestions) tracking.onEvent({ type: "gating_skipped", appointmentType })
    onContinue()
  }
  // Screener refusal → offers "back to questions"; booking-time (known-data) refusal → terminal.
  const onScreenerDisqualified = (exclusionMessageId: string) =>
    send({ type: "DISQUALIFIED", exclusionMessageId, fromScreener: true })
  const onBookingDisqualified = (exclusionMessageId: string) =>
    send({ type: "DISQUALIFIED", exclusionMessageId, fromScreener: false })
  const onBackFromPreferences = () => (hasScreeningQuestions ? send({ type: "BACK_TO_QUESTIONS" }) : onBackToEntry?.())
  const onConfirmBooking = (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) =>
    send({ type: "CONFIRM_BOOKING", preferences, slot, appointment })
  const onNeedsConsent = () => {
    tracking.onEvent({ type: "needs_consent" })
    send({ type: "NEEDS_CONSENT" })
  }

  if (!trackingReady) return null

  if (state.matches("insurance")) {
    return (
      <BookingBreadcrumbProvider stage="coverage" onReturn={onBackToEntry ?? onReturnToProviderResults}>
        <InsuranceBookingStep
          state={state}
          send={send}
          bridgeConfig={bridgeConfig}
          member={insuranceMember}
          onContinue={onContinue}
          onExit={onBackToEntry}
        />
      </BookingBreadcrumbProvider>
    )
  }

  if (state.matches("consent") || state.matches("paymentConsent"))
    return (
      <ConsentPage
        outstandingConsents={state.matches("paymentConsent") ? recoveryConsents : outstandingConsents}
        memberName={memberName}
        onContinue={state.matches("paymentConsent") ? onContinue : onConsentContinue}
      />
    )
  if (state.matches("healthQuestions"))
    return (
      <HealthQuestionsPage
        program={program}
        appointmentType={state.context.appointmentType}
        onContinue={onContinue}
        onDisqualified={onScreenerDisqualified}
      />
    )
  if (state.matches({ seeAvailability: "preferences" }))
    return (
      <SelectPreferencesPage
        program={program}
        onBack={onBackFromPreferences}
        onConfirmBooking={onConfirmBooking}
        enterprisePaymentsEnabled={enterprisePaymentsEnabled}
        onContinueToPayment={onContinueToPayment}
        prepareBooking={prepareBooking}
        paymentInitialization={{ isPending: initiatePayment.isLoading, isError: initiatePayment.isError }}
        onDisqualified={onBookingDisqualified}
        onNeedsConsent={onNeedsConsent}
        timeZone={timeZone}
      />
    )
  if (state.matches("payment"))
    return (
      <PaymentPage
        enabled={enterprisePaymentsEnabled}
        preferences={state.context.preferences}
        slot={state.context.selectedSlot}
        paymentMethod={state.context.paymentMethod}
        payment={state.context.payment}
        bookingId={state.context.bookingId}
        alreadyPaymentPreauthorized={state.context.paymentPreauthorized}
        onBookAppointment={async () => {
          const bookingId = actor.getSnapshot().context.bookingId
          if (!bookingId) throw new Error("Booking session unavailable")
          send({ type: "PAYMENT_PREAUTHORIZED" })
          try {
            const { preferences, selectedSlot } = actor.getSnapshot().context
            if (!preferences || !selectedSlot) throw new Error("Booking selection unavailable")
            const attemptNumber = tracking.onBookingSubmitted(appointmentType)
            const appointment = await bookAppointment.mutateAsync({
              program,
              bookingId,
              routingBackend: selectedSlot.routingBackend,
              slotToken: selectedSlot.slotToken,
              hostId: selectedSlot.availableHostIds[0],
              timeZone: getDisplayTimeZone(timeZone)
            })
            tracking.onEvent({
              type: "success",
              appointmentId: appointment.appointmentId,
              hostId: appointment.hostId,
              bookingAttemptNumber: attemptNumber
            })
            onConfirmBooking(preferences, selectedSlot, appointment)
          } catch (error) {
            const reason = bookingIneligibilityReason(error)
            if (reason !== null) {
              tracking.onEvent({ type: "gating_result", result: "ineligible", resultSource: "booking_recheck" })
              onBookingDisqualified(reason)
            } else if (isBookingConsentRequired(error)) {
              // ConsentPage snapshots documents on mount.
              setRecoveryConsents(await refreshOutstandingConsents())
              onNeedsConsent()
            } else if (isBookingSessionExpired(error)) {
              void queryClient.invalidateQueries({ queryKey: [AVAILABLE_DATES_QUERY_KEY] })
              void queryClient.invalidateQueries({ queryKey: [AVAILABILITY_FOR_DATE_QUERY_KEY] })
              send({ type: "PAYMENT_CANCELED" })
              send({ type: "BACK_TO_AVAILABILITY" })
            }
            throw error
          }
        }}
        onPaymentCanceled={() => send({ type: "PAYMENT_CANCELED" })}
        onBack={() => send({ type: "BACK_TO_AVAILABILITY" })}
      />
    )
  if (state.matches("photoId") && state.context.appointment)
    return <PhotoIdPage appointmentId={state.context.appointment.appointmentId} onContinue={onContinue} />
  if (state.matches("booked"))
    return (
      <BookedPage
        program={program}
        appointment={state.context.appointment}
        offeringId={state.context.preferences?.offeringId}
        timeZone={timeZone}
      />
    )
  if (state.matches("notEligible"))
    return (
      <NotEligiblePage
        exclusionMessageId={state.context.exclusionMessageId}
        onExploreCareOptions={onExploreCareOptions}
      />
    )
  return null
}
